import { useCallback, useEffect, useMemo, useState } from 'react';

import noop from 'lodash/noop';

import {
  useAccountGet,
  useApi,
  useSupportEnterFacility,
  useSupportFacilityOptions,
} from '@sbv-clients/data-access-api';
import { useQueryParamStore } from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  useFiltersStore,
  useParamStore,
  useScheduleStatusInitStore,
} from '@sbv-clients/shared-state';
import {
  Box,
  OverlayLoader,
  Select,
  SelectChangeEvent,
  SelectOption,
  Typography,
  useTheme,
} from '@sbv-clients/ui-shared';
import { FunctionComponent } from '@sbv-clients/util-misc';

type FacilitySelectOption = SelectOption<string>;

export const SupportFacilitySwitcher: FunctionComponent = () => {
  const theme = useTheme();
  const api = useApi();

  const { data: accountData } = useAccountGet();
  const { data: facilities, isLoading: isLoadingFacilities } =
    useSupportFacilityOptions();
  const { mutateAsync: enterFacility } = useSupportEnterFacility();
  const { onEnterFacility } = useAuth();
  const paramStore = useParamStore();
  const scheduleStatusInitStore = useScheduleStatusInitStore();

  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [isSwitching, setIsSwitching] = useState(false);

  const facilityOptions = useMemo<FacilitySelectOption[]>(
    () =>
      (facilities ?? []).map((f) => ({
        id: f.id as string,
        value: f.id as string,
        label: f.name ?? '',
      })),
    [facilities]
  );

  useEffect(() => {
    if (accountData?.tokenFacilityId) {
      setSelectedFacilityId(accountData.tokenFacilityId);
    }
  }, [accountData?.tokenFacilityId]);

  const handleChange = useCallback(
    async (event: SelectChangeEvent<unknown>) => {
      const nextFacilityId = event.target.value as string;
      if (!nextFacilityId || nextFacilityId === accountData?.tokenFacilityId) {
        return;
      }

      const previousFacilityId = selectedFacilityId;
      try {
        setSelectedFacilityId(nextFacilityId);
        setIsSwitching(true);

        // Release any open employee lock before switching facility.
        await api.employeeV2Api.apiV2EmployeesLocksMineDelete().catch(noop);

        const result = await enterFacility(nextFacilityId);
        if (result?.token && result?.refreshToken) {
          // onEnterFacility stores the new tokens WITHOUT writing a new
          // session stamp so the facility switch is not misread as a
          // Refresh Configuration event.
          onEnterFacility(result.token, result.refreshToken);
          paramStore.clearAllPages();
          scheduleStatusInitStore.reset();
          useQueryParamStore.getState().resetAll();
          useFiltersStore.getState().resetAll();
          window.location.replace('/');
        }
      } catch {
        setSelectedFacilityId(previousFacilityId);
      } finally {
        setIsSwitching(false);
      }
    },
    [
      accountData?.tokenFacilityId,
      api.employeeV2Api,
      enterFacility,
      onEnterFacility,
      paramStore,
      scheduleStatusInitStore,
      selectedFacilityId,
    ]
  );

  if (isLoadingFacilities || !facilityOptions.length) return null;

  return (
    <Box>
      {isSwitching && <OverlayLoader />}
      <Select
        sx={{
          backgroundColor: theme.background.lightGrey,
          boxShadow: 'none',
          width: { xs: '180px', sm: 'auto' },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            boxShadow: 'none',
          },
          '&:hover': { backgroundColor: theme.background.lightGrey3 },
        }}
        labelId="support-facility-select-label"
        value={selectedFacilityId}
        onChange={handleChange}
        disabled={isSwitching}
        options={facilityOptions}
        renderValue={(selected) => {
          const option = facilityOptions.find((o) => o.value === selected);
          if (!option) return '';
          return (
            <Typography
              sx={{
                marginTop: '-3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
              fontSize="16px"
              color={theme.text?.secondary}
            >
              {option.label}
            </Typography>
          );
        }}
      />
    </Box>
  );
};
